import { GameState } from '../game/GameState';
import type { BattleResult } from '../types/battle';
import type {
  ArmyState,
  CasusBelliId,
  FactionState,
  PeaceTermId,
  SiegeState,
  WarParticipationState,
  WarState,
} from '../types/game';
import { DataRegistry } from './DataRegistry';
import { SupplyManager } from './SupplyManager';

interface CasusBelliOption {
  id: CasusBelliId;
  label: string;
  description: string;
  warGoalProvinceId?: string;
  prestigeCost: number;
  exhaustionCost: number;
}

export class WarManager {
  static readonly instance = new WarManager();

  warBetween(firstFactionId: string, secondFactionId: string) {
    return Object.values(GameState.instance.requireCampaign().wars).find((war) => {
      const firstSide = war.attackers.includes(firstFactionId) || war.defenders.includes(firstFactionId);
      const secondSide = war.attackers.includes(secondFactionId) || war.defenders.includes(secondFactionId);
      return firstSide && secondSide && this.sideForFaction(war, firstFactionId) !== this.sideForFaction(war, secondFactionId);
    });
  }

  warsForFaction(factionId: string) {
    return Object.values(GameState.instance.requireCampaign().wars).filter((war) => war.attackers.includes(factionId) || war.defenders.includes(factionId));
  }

  sideForFaction(war: WarState, factionId: string): 'attacker' | 'defender' | undefined {
    if (war.attackers.includes(factionId)) return 'attacker';
    if (war.defenders.includes(factionId)) return 'defender';
    return undefined;
  }

  warScoreForFaction(war: WarState, factionId: string) {
    return this.sideForFaction(war, factionId) === 'attacker' ? war.warscore : -war.warscore;
  }

  availableCasusBelli(sourceFactionId: string, targetFactionId: string): CasusBelliOption[] {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const sourceData = DataRegistry.instance.factions[sourceFactionId];
    const targetData = DataRegistry.instance.factions[targetFactionId];
    if (!source || !sourceData || !targetData || sourceFactionId === targetFactionId) return [];

    const targetProvinces = Object.values(campaign.provinces).filter((province) => province.owner === targetFactionId);
    const claimedProvince = targetProvinces.find((province) => source.claims.includes(province.id));
    const borderProvince = targetProvinces.find((province) =>
      DataRegistry.instance.provinces[province.id]?.neighbors.some((neighborId) => campaign.provinces[neighborId]?.owner === sourceFactionId),
    );
    const tradeDispute = source.treaties.embargoes.includes(targetFactionId)
      || campaign.factions[targetFactionId]?.treaties.embargoes.includes(sourceFactionId);
    const religiousTarget = sourceData.stateReligion !== targetData.stateReligion;

    const options: CasusBelliOption[] = [];
    if (claimedProvince) {
      options.push({
        id: 'reconquest',
        label: 'Press Claim',
        description: `Recover the claimed province of ${DataRegistry.instance.provinces[claimedProvince.id]?.name ?? claimedProvince.id}.`,
        warGoalProvinceId: claimedProvince.id,
        prestigeCost: 0,
        exhaustionCost: 1,
      });
    }
    if (borderProvince) {
      options.push({
        id: 'conquest',
        label: 'Conquest',
        description: `Seize ${DataRegistry.instance.provinces[borderProvince.id]?.name ?? borderProvince.id} as a strategic war goal.`,
        warGoalProvinceId: borderProvince.id,
        prestigeCost: claimedProvince ? 1 : 4,
        exhaustionCost: claimedProvince ? 2 : 5,
      });
    }
    if (tradeDispute || source.treaties.trade.includes(targetFactionId)) {
      const targetPort = targetProvinces.find((province) => DataRegistry.instance.provinces[province.id]?.port);
      options.push({
        id: 'trade_dispute',
        label: 'Trade Dispute',
        description: 'Force reparations and commercial concessions by controlling an enemy port.',
        warGoalProvinceId: targetPort?.id,
        prestigeCost: 2,
        exhaustionCost: 3,
      });
    }
    if (religiousTarget) {
      options.push({
        id: 'religious_unification',
        label: 'Religious Unification',
        description: 'Break the opposing court and impose religious authority through victory.',
        warGoalProvinceId: borderProvince?.id ?? targetProvinces[0]?.id,
        prestigeCost: 3,
        exhaustionCost: 4,
      });
    }
    if (!options.length && targetProvinces[0]) {
      options.push({
        id: 'conquest',
        label: 'Conquest',
        description: 'Launch an expansionist war without a recognized claim.',
        warGoalProvinceId: targetProvinces[0].id,
        prestigeCost: 8,
        exhaustionCost: 8,
      });
    }
    return options;
  }

  declareWar(sourceFactionId: string, targetFactionId: string, casusBelli: CasusBelliId = 'conquest', warGoalProvinceId?: string) {
    const campaign = GameState.instance.requireCampaign();
    const source = campaign.factions[sourceFactionId];
    const target = campaign.factions[targetFactionId];
    if (!source || !target || sourceFactionId === targetFactionId || this.warBetween(sourceFactionId, targetFactionId)) return false;
    const truceTurn = source.truceUntil[targetFactionId] ?? 0;
    if (truceTurn > campaign.turn) {
      GameState.instance.log(`A truce remains in force for ${truceTurn - campaign.turn} month${truceTurn - campaign.turn === 1 ? '' : 's'}.`, 'warning');
      return false;
    }

    const option = this.availableCasusBelli(sourceFactionId, targetFactionId)
      .find((entry) => entry.id === casusBelli && (!warGoalProvinceId || entry.warGoalProvinceId === warGoalProvinceId))
      ?? this.availableCasusBelli(sourceFactionId, targetFactionId).find((entry) => entry.id === casusBelli)
      ?? this.availableCasusBelli(sourceFactionId, targetFactionId)[0];
    if (!option) return false;
    if (source.prestige < option.prestigeCost) {
      GameState.instance.log(`This war declaration requires ${option.prestigeCost} prestige.`, 'warning');
      return false;
    }

    source.prestige = Math.max(0, source.prestige - option.prestigeCost);
    source.warExhaustion = Math.min(100, source.warExhaustion + option.exhaustionCost);
    const attackers = this.collectWarSide(sourceFactionId, targetFactionId, 'attacker');
    const defenders = this.collectWarSide(targetFactionId, sourceFactionId, 'defender');
    const id = `war_${campaign.turn}_${sourceFactionId}_${targetFactionId}`;
    const participation = Object.fromEntries([...attackers, ...defenders].map((factionId) => [factionId, this.emptyParticipation()]));
    const war: WarState = {
      id,
      attackerLeader: sourceFactionId,
      defenderLeader: targetFactionId,
      attackers,
      defenders,
      casusBelli: option.id,
      warGoalProvinceId: warGoalProvinceId ?? option.warGoalProvinceId,
      startTurn: campaign.turn,
      warscore: 0,
      attackerBattleScore: 0,
      defenderBattleScore: 0,
      occupiedProvinces: [],
      blockadedProvinces: [],
      participation,
      lastMajorEvent: `${DataRegistry.instance.factions[sourceFactionId]?.name ?? sourceFactionId} declared war.`,
    };
    campaign.wars[id] = war;
    for (const attackerId of attackers) {
      for (const defenderId of defenders) this.setWarRelationship(attackerId, defenderId, true);
    }
    this.breakConflictingTreaties(attackers, defenders);
    GameState.instance.log(`${DataRegistry.instance.factions[sourceFactionId]?.name ?? sourceFactionId} declared a ${option.label.toLowerCase()} war on ${DataRegistry.instance.factions[targetFactionId]?.name ?? targetFactionId}.`, 'danger');
    return true;
  }

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    this.refreshBlockades();
    this.processManpowerRecovery();
    this.processReinforcementAndAttrition();
    this.processSieges();
    for (const war of Object.values(campaign.wars)) {
      this.recalculateWarScore(war);
      this.applyWarExhaustion(war);
      this.considerAutomaticPeace(war);
    }
  }

  beginSiege(armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const army = campaign.armies[armyId];
    const province = army ? campaign.provinces[army.provinceId] : undefined;
    if (!army || !province || province.owner === army.factionId || (province.fortLevel ?? 0) <= 0) return false;
    const war = this.warBetween(army.factionId, province.owner);
    if (!war || province.controller === army.factionId) return false;
    const defenders = Object.values(campaign.armies).filter((entry) => entry.provinceId === province.id && this.areEnemies(entry.factionId, army.factionId));
    if (defenders.length) return false;

    const existing = Object.values(campaign.sieges).find((siege) => siege.provinceId === province.id && siege.warId === war.id);
    if (existing) return true;
    const fortLevel = Math.max(1, province.fortLevel ?? 1);
    const siege: SiegeState = {
      id: `siege_${war.id}_${province.id}`,
      warId: war.id,
      provinceId: province.id,
      besiegerFactionId: army.factionId,
      defenderFactionId: province.owner,
      progress: 0,
      months: 0,
      garrison: 180 * fortLevel,
      blockade: Boolean(province.blockadedBy),
      breached: false,
    };
    campaign.sieges[siege.id] = siege;
    GameState.instance.log(`${DataRegistry.instance.provinces[province.id]?.name ?? province.id} is under siege.`, army.factionId === campaign.playerFactionId ? 'warning' : 'danger');
    return true;
  }

  siegeForProvince(provinceId: string) {
    return Object.values(GameState.instance.requireCampaign().sieges).find((siege) => siege.provinceId === provinceId);
  }

  isFortBlockingMovement(factionId: string, provinceId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province || (province.fortLevel ?? 0) <= 0 || province.controller === factionId || province.owner === factionId) return false;
    return this.areEnemies(factionId, province.owner) && Boolean(this.siegeForProvince(provinceId));
  }

  registerBattle(result: BattleResult) {
    const campaign = GameState.instance.requireCampaign();
    const war = this.warBetween(result.winnerFactionId, result.loserFactionId);
    if (!war) return;
    const winnerSide = this.sideForFaction(war, result.winnerFactionId);
    const battleValue = Math.max(3, Math.min(18, Math.round((result.attackerLosses + result.defenderLosses) / 8)));
    if (winnerSide === 'attacker') war.attackerBattleScore += battleValue;
    else war.defenderBattleScore += battleValue;
    const winner = this.ensureParticipation(war, result.winnerFactionId);
    const loser = this.ensureParticipation(war, result.loserFactionId);
    winner.battles += 1;
    winner.contribution += battleValue * 2;
    loser.battles += 1;
    winner.casualties += winnerSide === 'attacker' ? result.attackerLosses : result.defenderLosses;
    loser.casualties += winnerSide === 'attacker' ? result.defenderLosses : result.attackerLosses;
    const commander = Object.values(campaign.commanders).find((entry) => entry.armyId && campaign.armies[entry.armyId]?.factionId === result.winnerFactionId);
    if (commander) commander.victories += 1;
    const losingCommander = Object.values(campaign.commanders).find((entry) => entry.armyId && campaign.armies[entry.armyId]?.factionId === result.loserFactionId);
    if (losingCommander) losingCommander.defeats += 1;
    campaign.factions[result.winnerFactionId].militaryTradition = Math.min(100, campaign.factions[result.winnerFactionId].militaryTradition + 2);
    war.lastMajorEvent = `${DataRegistry.instance.factions[result.winnerFactionId]?.name ?? result.winnerFactionId} won the battle of ${DataRegistry.instance.provinces[result.provinceId]?.name ?? result.provinceId}.`;
    this.recalculateWarScore(war);
  }

  occupyProvince(provinceId: string, occupyingFactionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    if (!province || province.owner === occupyingFactionId) return false;
    const war = this.warBetween(occupyingFactionId, province.owner);
    if (!war) return false;
    province.controller = occupyingFactionId;
    province.unrest = Math.min(100, province.unrest + 7);
    province.loyalty = Math.max(5, province.loyalty - 8);
    province.devastation = Math.min(100, province.devastation + 4);
    if (!war.occupiedProvinces.includes(provinceId)) war.occupiedProvinces.push(provinceId);
    const participant = this.ensureParticipation(war, occupyingFactionId);
    participant.occupations += 1;
    participant.contribution += 10;
    war.lastMajorEvent = `${DataRegistry.instance.provinces[provinceId]?.name ?? provinceId} was occupied.`;
    this.recalculateWarScore(war);
    return true;
  }

  lootProvince(armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const army = campaign.armies[armyId];
    const province = army ? campaign.provinces[army.provinceId] : undefined;
    if (!army || !province || province.owner === army.factionId || !this.areEnemies(army.factionId, province.owner)) return false;
    if ((province.lootedTurn ?? -99) + 12 > campaign.turn) return false;
    const value = Math.max(8, Math.round(province.prosperity / 3 + province.population / 5));
    campaign.factions[army.factionId].treasury += value;
    province.prosperity = Math.max(0, province.prosperity - 8);
    province.devastation = Math.min(100, province.devastation + 12);
    province.unrest = Math.min(100, province.unrest + 10);
    province.lootedTurn = campaign.turn;
    campaign.factions[army.factionId].warExhaustion = Math.min(100, campaign.factions[army.factionId].warExhaustion + 2);
    GameState.instance.log(`${DataRegistry.instance.provinces[province.id]?.name ?? province.id} was looted for ${value} gold.`, army.factionId === campaign.playerFactionId ? 'success' : 'danger');
    return true;
  }

  peaceCost(term: PeaceTermId) {
    if (term === 'white_peace' || term === 'release_occupation') return 0;
    if (term === 'war_reparations') return 20;
    if (term === 'cede_war_goal') return 35;
    return 80;
  }

  canEnforcePeace(war: WarState, demandingFactionId: string, term: PeaceTermId) {
    const score = this.warScoreForFaction(war, demandingFactionId);
    if (term === 'white_peace' || term === 'release_occupation') return Math.abs(war.warscore) <= 20 || campaignAge(war) >= 24;
    return score >= this.peaceCost(term);
  }

  makePeace(demandingFactionId: string, targetFactionId: string, term: PeaceTermId) {
    const campaign = GameState.instance.requireCampaign();
    const war = this.warBetween(demandingFactionId, targetFactionId);
    if (!war || !this.canEnforcePeace(war, demandingFactionId, term)) {
      GameState.instance.log('The current warscore is insufficient for those peace terms.', 'warning');
      return false;
    }
    const demandingSide = this.sideForFaction(war, demandingFactionId);
    const winnerFactionId = demandingSide === 'attacker' ? war.attackerLeader : war.defenderLeader;
    const loserFactionId = demandingSide === 'attacker' ? war.defenderLeader : war.attackerLeader;
    const winner = campaign.factions[winnerFactionId];
    const loser = campaign.factions[loserFactionId];

    if (term === 'cede_war_goal' && war.warGoalProvinceId) {
      const province = campaign.provinces[war.warGoalProvinceId];
      if (province) {
        province.owner = winnerFactionId;
        province.controller = winnerFactionId;
        province.autonomy = Math.max(35, province.autonomy);
        province.loyalty = Math.min(province.loyalty, 45);
        winner.claims = winner.claims.filter((id) => id !== province.id);
      }
    } else if (term === 'war_reparations') {
      const payment = Math.min(loser.treasury, Math.max(60, Math.round(loser.treasury * 0.35)));
      loser.treasury -= payment;
      winner.treasury += payment;
    } else if (term === 'vassalize') {
      if (!winner.treaties.vassals.includes(loserFactionId)) winner.treaties.vassals.push(loserFactionId);
      loser.legitimacy = Math.max(10, loser.legitimacy - 15);
    }

    for (const provinceId of war.occupiedProvinces) {
      const province = campaign.provinces[provinceId];
      if (!province) continue;
      if (term !== 'cede_war_goal' || province.id !== war.warGoalProvinceId) province.controller = province.owner;
      delete province.blockadedBy;
    }
    for (const siege of Object.values(campaign.sieges)) {
      if (siege.warId === war.id) delete campaign.sieges[siege.id];
    }
    for (const attackerId of war.attackers) {
      for (const defenderId of war.defenders) {
        this.setWarRelationship(attackerId, defenderId, false);
        campaign.factions[attackerId].truceUntil[defenderId] = campaign.turn + 24;
        campaign.factions[defenderId].truceUntil[attackerId] = campaign.turn + 24;
      }
    }
    winner.prestige = Math.min(100, winner.prestige + (term === 'white_peace' ? 0 : 6));
    loser.prestige = Math.max(0, loser.prestige - (term === 'white_peace' ? 0 : 5));
    campaign.warHistory.unshift({
      id: war.id,
      attackerLeader: war.attackerLeader,
      defenderLeader: war.defenderLeader,
      startTurn: war.startTurn,
      endTurn: campaign.turn,
      result: term,
      victorFactionId: term === 'white_peace' ? undefined : winnerFactionId,
      warGoalProvinceId: war.warGoalProvinceId,
      finalWarscore: war.warscore,
    });
    campaign.warHistory = campaign.warHistory.slice(0, 40);
    delete campaign.wars[war.id];
    GameState.instance.log(`${DataRegistry.instance.factions[winnerFactionId]?.name ?? winnerFactionId} concluded peace with ${DataRegistry.instance.factions[loserFactionId]?.name ?? loserFactionId}: ${term.replaceAll('_', ' ')}.`, 'success');
    return true;
  }

  targetForWar(war: WarState, factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const side = this.sideForFaction(war, factionId);
    if (side === 'attacker' && war.warGoalProvinceId) return campaign.provinces[war.warGoalProvinceId];
    const enemyIds = side === 'attacker' ? war.defenders : war.attackers;
    return Object.values(campaign.provinces)
      .filter((province) => enemyIds.includes(province.owner))
      .sort((a, b) => (b.fortLevel ?? 0) - (a.fortLevel ?? 0) || b.population - a.population)[0];
  }

  areEnemies(firstFactionId: string, secondFactionId: string) {
    return Boolean(this.warBetween(firstFactionId, secondFactionId));
  }

  private collectWarSide(leaderId: string, enemyLeaderId: string, role: 'attacker' | 'defender') {
    const campaign = GameState.instance.requireCampaign();
    const leader = campaign.factions[leaderId];
    const participants = new Set<string>([leaderId]);
    const possibleAllies = role === 'defender'
      ? [...(leader.treaties.defensivePacts ?? []), ...(leader.treaties.alliances ?? [])]
      : [...(leader.treaties.alliances ?? [])];
    for (const allyId of possibleAllies) {
      if (!campaign.factions[allyId] || campaign.factions[allyId].atWarWith.includes(enemyLeaderId)) continue;
      if (campaign.factions[allyId].treaties.alliances.includes(enemyLeaderId)) continue;
      participants.add(allyId);
    }
    return [...participants];
  }

  private breakConflictingTreaties(attackers: string[], defenders: string[]) {
    const campaign = GameState.instance.requireCampaign();
    const treatyKeys = ['alliances', 'defensivePacts', 'nonAggression', 'trade', 'militaryAccess', 'marriages'];
    for (const attackerId of attackers) {
      for (const defenderId of defenders) {
        for (const key of treatyKeys) {
          campaign.factions[attackerId].treaties[key] = (campaign.factions[attackerId].treaties[key] ?? []).filter((id) => id !== defenderId);
          campaign.factions[defenderId].treaties[key] = (campaign.factions[defenderId].treaties[key] ?? []).filter((id) => id !== attackerId);
        }
      }
    }
  }

  private setWarRelationship(firstFactionId: string, secondFactionId: string, active: boolean) {
    const campaign = GameState.instance.requireCampaign();
    const first = campaign.factions[firstFactionId];
    const second = campaign.factions[secondFactionId];
    if (!first || !second) return;
    if (active) {
      if (!first.atWarWith.includes(secondFactionId)) first.atWarWith.push(secondFactionId);
      if (!second.atWarWith.includes(firstFactionId)) second.atWarWith.push(firstFactionId);
    } else {
      first.atWarWith = first.atWarWith.filter((id) => id !== secondFactionId);
      second.atWarWith = second.atWarWith.filter((id) => id !== firstFactionId);
    }
  }

  private processManpowerRecovery() {
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      const provinces = Object.values(campaign.provinces).filter((province) => province.owner === faction.id && province.controller === faction.id);
      const soldiers = provinces.reduce((sum, province) => sum + (province.populationGroups.soldiers ?? 0), 0);
      const recovery = Math.max(4, Math.round(soldiers * (0.06 + faction.militaryTradition / 2000)));
      faction.maxManpower = Math.max(faction.maxManpower, Math.round(provinces.reduce((sum, province) => sum + province.population, 0) * 22));
      faction.manpower = Math.min(faction.maxManpower, faction.manpower + recovery);
      if (!faction.atWarWith.length) faction.warExhaustion = Math.max(0, faction.warExhaustion - 2);
      faction.militaryTradition = Math.max(0, faction.militaryTradition - 0.2);
    }
  }

  private processReinforcementAndAttrition() {
    const campaign = GameState.instance.requireCampaign();
    for (const army of Object.values(campaign.armies)) {
      const faction = campaign.factions[army.factionId];
      const province = campaign.provinces[army.provinceId];
      if (!faction || !province) continue;
      const hostile = province.owner !== army.factionId && this.areEnemies(army.factionId, province.owner);
      const commander = army.commanderId ? campaign.commanders[army.commanderId] : undefined;
      const logisticsReduction = commander ? commander.logistics * 0.008 : 0;
      const capacity = SupplyManager.instance.provinceSupplyCapacity(province.id);
      const demand = army.units.reduce((sum, stack) => sum + (DataRegistry.instance.units[stack.unitId]?.supply ?? 1), 0);
      const overcrowding = Math.max(0, demand - capacity);
      const seasonalDisease = hostile && campaign.month >= 10 ? 0.012 : 0;
      const attritionRate = Math.max(0, (hostile ? 0.012 : 0) + overcrowding * 0.002 + seasonalDisease - logisticsReduction);
      let losses = 0;
      if (attritionRate > 0) {
        for (const stack of army.units) {
          const loss = Math.min(stack.strength, Math.max(0, Math.round(stack.strength * attritionRate)));
          stack.strength -= loss;
          stack.morale = Math.max(1, stack.morale - Math.ceil(attritionRate * 100));
          losses += loss;
        }
      }
      army.lastAttrition = losses;
      army.units = army.units.filter((stack) => stack.strength > 0);
      if (!army.units.length) {
        delete campaign.armies[army.id];
        continue;
      }

      if (!hostile && province.controller === army.factionId && faction.manpower > 0) {
        for (const stack of army.units) {
          const unit = DataRegistry.instance.units[stack.unitId];
          if (!unit) continue;
          const missing = Math.max(0, unit.manpower - stack.strength);
          const reinforced = Math.min(missing, Math.max(0, Math.round(4 + faction.militaryTradition / 12)), faction.manpower);
          stack.strength += reinforced;
          faction.manpower -= reinforced;
        }
      }
      if (losses > 0 && army.factionId === campaign.playerFactionId) GameState.instance.log(`${army.id} lost ${losses} troops to attrition and disease.`, 'warning');
    }
  }

  private processSieges() {
    const campaign = GameState.instance.requireCampaign();
    for (const army of Object.values(campaign.armies)) this.beginSiege(army.id);
    for (const siege of Object.values(campaign.sieges)) {
      const war = campaign.wars[siege.warId];
      const province = campaign.provinces[siege.provinceId];
      if (!war || !province || province.controller === siege.besiegerFactionId || !this.areEnemies(siege.besiegerFactionId, siege.defenderFactionId)) {
        delete campaign.sieges[siege.id];
        continue;
      }
      const besiegers = Object.values(campaign.armies).filter((army) => army.provinceId === siege.provinceId && army.factionId === siege.besiegerFactionId);
      const relief = Object.values(campaign.armies).filter((army) => army.provinceId === siege.provinceId && this.areEnemies(army.factionId, siege.besiegerFactionId));
      if (!besiegers.length || relief.length) {
        siege.progress = Math.max(0, siege.progress - 10);
        continue;
      }
      const strength = besiegers.reduce((sum, army) => sum + army.units.reduce((armySum, stack) => armySum + stack.strength, 0), 0);
      const siegeSkill = Math.max(0, ...besiegers.map((army) => army.commanderId ? campaign.commanders[army.commanderId]?.siege ?? 0 : 0));
      siege.blockade = Boolean(province.blockadedBy);
      siege.months += 1;
      const fortResistance = Math.max(1, province.fortLevel ?? 1) * 4;
      const gain = Math.max(2, Math.round(4 + strength / 900 + siegeSkill * 1.5 + (siege.blockade ? 5 : 0) - fortResistance));
      siege.progress = Math.min(100, siege.progress + gain);
      siege.garrison = Math.max(0, siege.garrison - Math.round(strength / 85 + (siege.blockade ? 12 : 4)));
      if (!siege.breached && siege.progress >= 55) siege.breached = true;
      province.devastation = Math.min(100, province.devastation + 1 + (siege.blockade ? 1 : 0));
      if (siege.progress >= 100 || siege.garrison <= 0) {
        this.occupyProvince(siege.provinceId, siege.besiegerFactionId);
        campaign.factions[siege.besiegerFactionId].militaryTradition = Math.min(100, campaign.factions[siege.besiegerFactionId].militaryTradition + 3);
        GameState.instance.log(`${DataRegistry.instance.provinces[siege.provinceId]?.name ?? siege.provinceId} surrendered after ${siege.months} month${siege.months === 1 ? '' : 's'}.`, siege.besiegerFactionId === campaign.playerFactionId ? 'success' : 'danger');
        delete campaign.sieges[siege.id];
      }
    }
  }

  private refreshBlockades() {
    const campaign = GameState.instance.requireCampaign();
    for (const province of Object.values(campaign.provinces)) delete province.blockadedBy;
    for (const province of Object.values(campaign.provinces)) {
      if (!DataRegistry.instance.provinces[province.id]?.port) continue;
      const hostileArmy = Object.values(campaign.armies).find((army) => army.provinceId === province.id && this.areEnemies(army.factionId, province.owner));
      if (hostileArmy) province.blockadedBy = hostileArmy.factionId;
    }
  }

  private recalculateWarScore(war: WarState) {
    const campaign = GameState.instance.requireCampaign();
    let score = war.attackerBattleScore - war.defenderBattleScore;
    for (const provinceId of war.occupiedProvinces) {
      const province = campaign.provinces[provinceId];
      if (!province || province.controller === province.owner) continue;
      const occupierSide = this.sideForFaction(war, province.controller);
      const value = provinceId === war.warGoalProvinceId ? 28 : Math.min(14, 4 + Math.round(province.population / 18) + (province.fortLevel ?? 0) * 2);
      score += occupierSide === 'attacker' ? value : -value;
    }
    if (war.warGoalProvinceId) {
      const goal = campaign.provinces[war.warGoalProvinceId];
      if (goal && goal.controller !== goal.owner) score += this.sideForFaction(war, goal.controller) === 'attacker' ? 10 : -10;
    }
    war.warscore = Math.max(-100, Math.min(100, Math.round(score)));
  }

  private applyWarExhaustion(war: WarState) {
    const campaign = GameState.instance.requireCampaign();
    for (const factionId of [...war.attackers, ...war.defenders]) {
      const faction = campaign.factions[factionId];
      if (!faction) continue;
      const occupiedHome = war.occupiedProvinces.filter((provinceId) => campaign.provinces[provinceId]?.owner === factionId).length;
      const attrition = Object.values(campaign.armies).filter((army) => army.factionId === factionId).reduce((sum, army) => sum + (army.lastAttrition ?? 0), 0);
      faction.warExhaustion = Math.min(100, faction.warExhaustion + 0.5 + occupiedHome * 0.8 + attrition / 400);
    }
  }

  private considerAutomaticPeace(war: WarState) {
    const campaign = GameState.instance.requireCampaign();
    const age = campaign.turn - war.startTurn;
    if (!campaign.wars[war.id] || age < 6) return;
    if (war.warscore >= 70 && war.attackerLeader !== campaign.playerFactionId) this.makePeace(war.attackerLeader, war.defenderLeader, war.warGoalProvinceId ? 'cede_war_goal' : 'war_reparations');
    else if (war.warscore <= -70 && war.defenderLeader !== campaign.playerFactionId) this.makePeace(war.defenderLeader, war.attackerLeader, 'war_reparations');
    else if (age >= 36 && Math.abs(war.warscore) <= 15 && war.attackerLeader !== campaign.playerFactionId && war.defenderLeader !== campaign.playerFactionId) this.makePeace(war.attackerLeader, war.defenderLeader, 'white_peace');
  }

  private emptyParticipation(): WarParticipationState {
    return { battles: 0, casualties: 0, occupations: 0, contribution: 0 };
  }

  private ensureParticipation(war: WarState, factionId: string) {
    war.participation[factionId] ??= this.emptyParticipation();
    return war.participation[factionId];
  }
}

function campaignAge(war: WarState) {
  return GameState.instance.requireCampaign().turn - war.startTurn;
}
